import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Constants } from '@pwc/pwc-deals-web-common';
import { Button, Icon, MiniLoader } from '@pwc/pwc-deals-ui';
import { useDispatch } from 'react-redux';
import ExcelPPTLibraryItemCard from 'features/analyses/common/LibraryItemCard/ExcelPPTLibraryItemCard';
import {
  setShowPowerPivotNotSupportedModal,
  setShowSharePointModalPowerPivot,
} from 'features/analyses/common/LibraryItemCard/PowerPivot/redux/powerPivotSlice';
import DeleteButton from '../../../common/components/SectionLayout/LeftPanel/Section/DeleteButton/DeleteButton';
import {
  clearClipboard,
  getLibraryItemsFromClipboard,
} from '../../contentLibrary/libraryItemService';
import useMobile from '../../../common/hooks/useMobile';
import { showModalError } from '../../../common/redux/errors/errorsSlice';
import { UNABLE_TO_ADD_OBJECT } from '../../../common/components/ModalError/constants';
import LibraryItemCard from '../../analyses/common/LibraryItemCard/LibraryItemCard';
import './Thumbnail.scss';
import { isPowerPivotLibraryItem } from '../../analyses/common/LibraryItemCard/PowerPivot/constants/PowerPivotTypes';
import PowerPivotNotSupportedModal from '../../analyses/common/LibraryItemCard/PowerPivot/components/PowerPivotNotSupportedModal.tsx';
import {
  THUMBNAIL,
  UNABLE_TO_ADD_CONTENT,
} from '../../analyses/common/LibraryItemCard/PowerPivot/constants/PowerPivotErrorMessages';
import {
  HIDE_LOST_COMMENTS_DIALOG_SESSION_STORAGE,
  LostCommentsWarningDialog,
} from '../../../common/components/LostCommentsWarningDialog';

const Thumbnail = ({
  projectId,
  setNoDataOnClipboard,
  updateThumbnail,
  thumbnail,
  editable = true,
  showAutoSync = true,
  onSummary = false,
  loading = false,
}) => {
  const isMobile = useMobile();
  const dispatch = useDispatch();
  const [clearSectionHover, setClearSectionHover] = useState(false);
  const { t } = useTranslation('');
  const [mouseOver, setMouseOver] = useState(false);
  const [showLostCommentsModal, setShowLostCommentsModal] = useState(false);
  const isLostCommentsModalHidden =
    window.sessionStorage.getItem(HIDE_LOST_COMMENTS_DIALOG_SESSION_STORAGE) ===
    'true';

  const addThumbnailClick = async () => {
    try {
      const libraryItemResponse = await getLibraryItemsFromClipboard(projectId);
      if (!libraryItemResponse) {
        setNoDataOnClipboard(true);
        dispatch(setShowSharePointModalPowerPivot(false));
        return;
      }

      const { data, id, name, status, type } = libraryItemResponse;

      if (isPowerPivotLibraryItem(type)) {
        dispatch(setShowPowerPivotNotSupportedModal(true));
        return;
      }

      let libraryItemData = {
        GroupedByKey: data.driveId,
        DriveId: data.driveId,
        DriveItemId: data.driveItemId,
        SharePointItemUrl: data.sharePointItemUrl,
        SiteId: data.siteId,
      };
      switch (type) {
        case Constants.LIBRARY_ITEM_TYPES.EXCEL_CHART.value:
          libraryItemData = {
            ...libraryItemData,
            WorksheetName: data.worksheetName,
            UniqueId: data.uniqueId,
            ChartName: data.chartName,
          };
          break;
        case Constants.LIBRARY_ITEM_TYPES.EXCEL_NAMED_RANGE.value:
        case Constants.LIBRARY_ITEM_TYPES.EXCEL_CELL_RANGE.value:
          libraryItemData = {
            ...libraryItemData,
            FormatType: data.formatType,
            Name: data.name,
            UniqueId: data.uniqueId,
            WorksheetName: data.worksheetName,
          };
          break;
        case Constants.LIBRARY_ITEM_TYPES.POWER_POINT_GROUP.value:
          libraryItemData = {
            ...libraryItemData,
            SlideNumber: data.slideNumber,
            GroupName: data.groupName,
          };
          break;
        default:
          break;
      }

      const libraryItemCreated = {
        data: JSON.stringify(libraryItemData),
        formatType: data.formatType,
        id,
        name,
        status,
        type,
      };

      updateThumbnail(libraryItemCreated);

      // clear the clipboard
      await clearClipboard(projectId, libraryItemResponse);
    } catch (e) {
      dispatch(showModalError(UNABLE_TO_ADD_OBJECT));
    }
  };
  const hideClearSectionButton = () => {
    setMouseOver(false);
  };

  const renderThumbnail = () => (
    <LibraryItemCard
      libraryItemId={thumbnail.id}
      isCommentingEnabled={!onSummary}
    >
      <ExcelPPTLibraryItemCard
        libraryItem={thumbnail}
        projectId={projectId}
        setIsDragDisabled={() => {}}
        showAutoSync={showAutoSync}
        isKfaThumbnail
      />
    </LibraryItemCard>
  );

  if (!editable) {
    return thumbnail ? (
      <div className={onSummary ? 'summary-thumbnail' : 'readonly-thumbnail'}>
        {renderThumbnail()}
      </div>
    ) : null;
  }

  return (
    <div
      className={onSummary ? '' : 'thumbnail-kfa-container'}
      onMouseEnter={() => setMouseOver(true)}
      onFocus={() => setMouseOver(true)}
      onMouseLeave={hideClearSectionButton}
      onBlur={hideClearSectionButton}
      style={{ position: 'relative' }}
    >
      <div className="d-flex mb-3">
        <div className="mr-3">
          <Icon name="revenue" width="16" />
        </div>
        <div className="block-title">{t('thumbnail')}</div>
      </div>
      <div className="thumbnail-wrapper">
        <>
          {thumbnail && !loading ? (
            renderThumbnail()
          ) : (
            <>
              <div className="thumbnail-content">
                {loading ? (
                  <MiniLoader />
                ) : (
                  <div>
                    <div>
                      <Button
                        className="ui-button button-link object-injest-toggle"
                        onClick={addThumbnailClick}
                        type="button"
                      >
                        <Icon
                          name="plus"
                          width={24}
                          className="outline icon-orange"
                        />
                      </Button>
                    </div>
                    <div className="mt-2 add-thumbnail-label">
                      {t('addAChartOrTable')}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      </div>

      {thumbnail && !isMobile && (
        <div className="d-flex justify-content-end">
          <div
            className="clearThumbnailWrapper"
            style={{
              display: mouseOver ? 'flex' : 'none',
            }}
          >
            <DeleteButton
              tooltip="Clear thumbnail"
              tooltipPlacement="top"
              iconType={`${
                clearSectionHover ? 'fill' : 'outline'
              } icon-grey-theme`}
              handleClick={() => {
                if (isLostCommentsModalHidden) {
                  updateThumbnail();
                } else {
                  setShowLostCommentsModal(true);
                }
              }}
              handleMouseEnter={() => setClearSectionHover(true)}
              handleMouseLeave={() => setClearSectionHover(false)}
            />
            <LostCommentsWarningDialog
              open={showLostCommentsModal}
              onConfirm={() => {
                setShowLostCommentsModal(false);
                updateThumbnail();
              }}
              onClose={() => {
                setShowLostCommentsModal(false);
              }}
            />
          </div>
        </div>
      )}

      {!loading && (
        <PowerPivotNotSupportedModal
          title={t(UNABLE_TO_ADD_CONTENT)}
          message={t(THUMBNAIL)}
        />
      )}
    </div>
  );
};

export default Thumbnail;
